"use client";

import { useEffect, useRef, useState } from "react";

declare global {
  interface Window {
    instgrm?: { Embeds: { process: () => void } };
  }
}

const EMBED_SCRIPT = "https://www.instagram.com/embed.js";

/**
 * Instagram draws its embed no narrower than 326px, and at that width a reel
 * card stands well over 700px tall. So the embed is always drawn at its own
 * natural width and scaled, as a whole, to whatever width the grid cell it
 * sits in turns out to be — the cell decides the size, not a number in here,
 * which is what lets the three cards share one fluid row.
 */
const EMBED_WIDTH = 326;
/**
 * Until the cell has been measured; replaced on the first observation. The
 * card is Instagram's own 326px width, or less on a phone.
 */
const FALLBACK_WIDTH = EMBED_WIDTH;

let scriptPromise: Promise<void> | null = null;

/** Loads Instagram's embed script once for the whole page. */
function loadEmbedScript(): Promise<void> {
  if (window.instgrm) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = EMBED_SCRIPT;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error("instagram embed script failed to load"));
    };
    document.body.appendChild(script);
  });
  return scriptPromise;
}

/**
 * One embed slot. Instagram's script replaces the blockquote with an iframe, so
 * the blockquote is built with DOM calls inside a node React renders empty and
 * never touches again — if React owned it, the next render would fight the swap.
 * No HTML strings: the link goes in through setAttribute and the fallback text
 * through textContent. The link inside is what a visitor sees if the script is
 * blocked.
 */
function EmbedSlot({ url }: { url: string }) {
  const frameRef = useRef<HTMLDivElement>(null);
  const slotRef = useRef<HTMLDivElement>(null);
  /**
   * The card is a fixed frame in Instagram's own 326 × 613 card shape, scaled
   * evenly (never stretched) to the cell's width, so the whole embed shows —
   * header to "Add a comment" — with square corners and no crop.
   */
  const [width, setWidth] = useState(FALLBACK_WIDTH);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    const onFrame = new ResizeObserver(() => setWidth(frame.clientWidth || FALLBACK_WIDTH));
    onFrame.observe(frame);
    return () => onFrame.disconnect();
  }, []);

  const scale = width / EMBED_WIDTH;

  useEffect(() => {
    const slot = slotRef.current;
    if (!slot) return;
    const quote = document.createElement("blockquote");
    quote.className = "instagram-media";
    quote.setAttribute("data-instgrm-permalink", url);
    quote.setAttribute("data-instgrm-version", "14");
    quote.style.cssText =
      `background:#fff;border:0;border-radius:0;box-shadow:none;margin:0;max-width:540px;min-width:${EMBED_WIDTH}px;width:100%;padding:0;`;
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.textContent = "View this post on Instagram";
    link.style.cssText =
      "display:block;padding:24px;font:600 14px/1.4 sans-serif;color:#0095f6;text-decoration:none;text-align:center;";
    quote.appendChild(link);
    slot.replaceChildren(quote);
    // Late arrival: the script is already on the page, so draw this one now.
    window.instgrm?.Embeds.process();
    return () => slot.replaceChildren();
  }, [url]);

  return (
    // The frame's size is fixed by the grid and its 9:16 ratio alone: the
    // embed is absolutely placed inside it, so Instagram's own 326px-wide,
    // 600px-tall card (and the height it settles on after loading) can never
    // push the frame larger or shift the layout.
    <div ref={frameRef} className="relative aspect-[326/613] w-full overflow-hidden bg-white">
      <div
        ref={slotRef}
        className="absolute top-0 left-0 min-h-[520px] origin-top-left"
        style={{ width: EMBED_WIDTH, transform: `scale(${scale})` }}
      />
    </div>
  );
}

/**
 * The latest posts from @greentourismpakistan, as Instagram's own embeds.
 *
 * Instagram's script is heavy and sets its own cookies, and this section sits
 * well below the fold on a page whose load time is watched closely. So nothing
 * from Instagram loads until the section is about to scroll into view; before
 * that each card is a fixed-height placeholder, which also keeps the page from
 * jumping when the embeds arrive.
 */
/**
 * The row of embeds on its own, without a heading: the social wall places the
 * Instagram column and the YouTube column side by side and owns the headings.
 */
export function InstagramPostRow({
  posts,
  profileUrl,
  gridClassName = "grid grid-cols-[repeat(3,minmax(0,var(--reel-w,104px)))] justify-center gap-[var(--reel-gap,16px)]",
}: {
  posts: string[];
  profileUrl: string;
  /** The layout of the embeds; the social section's single card passes its own. */
  gridClassName?: string;
}) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [near, setNear] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setNear(true);
          observer.disconnect();
        }
      },
      { rootMargin: "800px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const postsKey = posts.join(" ");
  useEffect(() => {
    if (!near || !postsKey) return;
    let cancelled = false;
    loadEmbedScript()
      .then(() => {
        if (!cancelled) window.instgrm?.Embeds.process();
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [near, postsKey]);

  if (posts.length === 0) return null;

  return (
    <div ref={sectionRef}>
      {/* Three across at every width, each capped at the wall's --reel-w and
          never wider; on a phone they shrink evenly to fit, 9:16 kept. Six
          posts make the 3 × 2 grid. */}
      <div className={gridClassName}>
        {posts.map((url) => (
          <EmbedSlot key={url} url={url} />
        ))}
      </div>

      {failed ? (
        <p className="mt-2 mb-0 text-sm text-[var(--muted)]">
          Instagram could not be loaded right now.{" "}
          <a href={profileUrl} target="_blank" rel="noopener noreferrer" className="text-[var(--accent)] underline">
            See the posts on Instagram
          </a>
          .
        </p>
      ) : null}
    </div>
  );
}
